/**
 * Events menu: clickable list of events/custom events to go inside mp-drop-menu
 */
import produce from 'immer';
import sum from 'lodash/sum';

import {assertUnreachable} from 'common/util/typescript';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {ProjectFeatureFlag, ResourceType} from 'common/report/constants';
import {getIconForEvent} from 'common/report/util/events';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {debounce} from 'common/util/lodash-wrapped';
import {TRACKING_EVENTS} from 'common/query-builder/query-block/constants';
import {SORT_OPTION_ITEMS} from 'common/query-builder/query-block/properties-menu/constants';
import {ExpanderItemClickEvent, ItemClickEvent, ItemFocusEvent, Section} from 'common/components/items-menu/types';
import {promptCustomEvents} from 'common/widgets/custom-event-modal/utils';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {InputComponent} from 'common/components/input';
import {DummyStorageManager} from 'common/contexts/storage-manager/dummy';
import {captureWorkflowTiming} from 'common/util/workflow-timing';
import {featureSamplingUtils} from 'common/report/util/feature-sampling';

import {ChildMenu} from '../child-menu';
import {EventsController, getCustomEventMenuItem, getEventMenuItem} from '../contexts/events-controller';
import {
  FilterMenuChangeEvent,
  FilterValue,
  SortBy,
  SortMenuChangeEvent,
  SortSectionItem,
} from '../properties-menu/types';
import {getOpenedMenuComponent, OpenedMenuComponent} from '../utils/util';
import {template} from './template';
import {
  ActionType,
  Event,
  EventItem,
  EventMenuItem,
  MenuType,
  ModalType,
  SelectEventItem,
  StageableMenuItem,
} from './types';
import {filterToBookmarkableEventItem, getEventSelectedLabel} from './util';
import {renderEventItemContextPanel} from './context-panel';
import {AllEventsFilter} from './constants';

interface EventsMenuState {
  eventData: EventItem[];
  isMenuLoading: boolean;
  isMenuOpen: boolean;
  permissions: Set<string>;
  stagedItemsStack: StageableMenuItem[];
  includeHidden: boolean;
  sortByValue: SortBy;
  searchFilter: string;
  tooltipData: ContextPanelData;
  selectedTagFilter: FilterValue;
}
export class EventsMenu extends ChildMenu<EventsMenuState> {
  static contextPanelRenderer = renderEventItemContextPanel;
  public searchInputEl: InputComponent = null;
  private _openedAt: number = null;
  private _openCount = 0;
  private _numTotalItemsOnPrevRender: number = null;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'other-options': {
        type: `json`,
        default: [],
        description: `list of items to show in the other section`,
      },
      'show-any-event': `boolean`,
      'show-session-event': `boolean`,
      'show-all-events': `boolean`,
      'show-top-events': `boolean`,
    };
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    // set open before any other code executes.
    this.setOpenedAt();

    super.attributeChangedCallback(attr, oldValue, newValue);

    if (attr === `open`) {
      this.update({
        includeHidden: false,
        sortByValue: this.config.defaultState.sortByValue,
      });

      if (this.attr(`open`)) {
        this.trackEvent(TRACKING_EVENTS.OPEN_EVENTS_DROPDOWN);
      } else {
        // when events-menu closes, all context menu also closes.
        this.dispatchEvent(new CustomEvent(`closeContextMenu`, {detail: {isOpen: false}, bubbles: true}));
      }
    }
  }

  connectedCallback() {
    this.setOpenedAt();
    super.connectedCallback();
  }

  /**
   * See comment for analogous functionality in <mp-properties-menu>
   */
  setOpenedAt() {
    // setOpenedAt may be called before this.attr managed state is available.
    if (this.isAttributeEnabled(`open`)) {
      if (!this._openedAt) {
        this._openedAt = new Date().getTime();
        this._openCount += 1;

        let openedComponent: OpenedMenuComponent;
        captureWorkflowTiming(this, {
          name: `Events Menu`,
          key: `events-menu`,
          isComplete: (ev) => {
            openedComponent = getOpenedMenuComponent(ev, this);
            return Boolean(openedComponent);
          },
          additionalProps: () => ({
            openCount: this._openCount,
            openedComponent,
          }),
        });
        captureWorkflowTiming(this, {
          name: `Events Menu Loaded`,
          key: `events-menu-loaded`,
          isComplete: (ev) => getOpenedMenuComponent(ev, this) === OpenedMenuComponent.ItemsMenu,
          additionalProps: () => ({
            numTotalItems: this._numTotalItemsOnPrevRender,
            openCount: this._openCount,
          }),
        });
      }
    } else {
      this._openedAt = null;
    }
  }

  get config() {
    return {
      template,

      contexts: [`eventsController` as const, `sessionStorageManager` as const, `currentProject` as const],
      defaultContexts: {
        eventsController: new EventsController(),
        sessionStorageManager: new DummyStorageManager(),
      },

      defaultState: {
        eventData: [],
        tooltipData: null,
        includeHidden: false,
        stagedItemsStack: [],
        sortByValue: SortBy.Relevance,
        tags: [],
        selectedTagFilter: AllEventsFilter.value,
      },

      helpers: {
        ...super.config.helpers,
        getMenuSections: () => {
          const sections = this.getSections();
          this._numTotalItemsOnPrevRender = sum(sections.map((section) => section.items.length));
          return sections;
        },
        isLoading: () => this.getContext(`eventsController`).loading,
        getSortingOptionSectionItems: (): SortSectionItem[] => {
          return SORT_OPTION_ITEMS.map((option) => ({
            ...option,
            isSelected: option.value === this.state.sortByValue,
          }));
        },
        handleTagFilterChange: ({detail}: FilterMenuChangeEvent) => {
          const selectedTagFilter = detail.item.value;
          this.update({selectedTagFilter});
          this.trackEvent(TRACKING_EVENTS.CHANGED_FILTER, {filter: selectedTagFilter});
        },
        handleSortChange: ({detail}: SortMenuChangeEvent) => {
          const sortByValue = detail.item.value;
          this.update({sortByValue});
          this.trackEvent(TRACKING_EVENTS.CHANGED_SORTING, {'sorting type': sortByValue});
        },
        handleSearchInput: (searchInput) => this.debouncedSearchInput(searchInput),

        canCreateItems: (): boolean => {
          return (
            !this.attr(`exclude-custom-events`) &&
            !this.attr(`hide-edit-controls`) &&
            this.getContext(`eventsController`).canWriteCustomEvents()
          );
        },

        getSearchPlaceholder: () => {
          let placeholder = `Search...`;
          const stagedItem = this.getStagedItem();
          if (stagedItem?.action === ActionType.StageMenu) {
            switch (stagedItem.menuType) {
              case MenuType.Tags:
                placeholder = `Search Tags`;
                break;
              case MenuType.Tag:
                placeholder = `Search ${stagedItem.value}`;
                break;
            }
          }
          return placeholder;
        },

        /** appends a manual event entry item */
        sectionsFromInput: (searchFilter): Array<Section<EventItem>> => {
          if (this.getStagedItem()?.menuType === MenuType.Tags) {
            return [];
          }
          return [
            {
              items: [
                {
                  label: `"${searchFilter}"`,
                  value: {
                    value: searchFilter,
                    label: searchFilter,
                  },
                  itemFromInput: true,
                },
              ],
            },
          ];
        },

        // This is for the Create Custom button at the bottom of the menu
        triggerModal: () => this.triggerModal(ModalType.CreateCustomEvent),

        shouldShowRefreshButton: () => {
          const currentProject = this.getContext(`currentProject`).get().value;
          return (
            currentProject?.hasFeatureFlag(ProjectFeatureFlag.EventsMenuRefresh) ||
            featureSamplingUtils.determineFeatureEligibility(
              currentProject?.id.toString(),
              ProjectFeatureFlag.EventsMenuRefresh,
              20,
            )
          );
        },
        handleRefreshButtonClick: () => {
          this.getContext(`eventsController`).refreshData();
          this.trackEvent(TRACKING_EVENTS.REFRESHED_EVENTS);
        },
      },
    };
  }

  getItemUniqueKey(item: EventItem) {
    return item.value;
  }

  handleCustomEventDelete(deletedId: number) {
    if (deletedId && this.attr(`selected`)?.id === deletedId) {
      // if deleting the selected custom event we delete the entire block
      this.dispatchEvent(new CustomEvent(`delete`));
    }
  }

  handleCloseMenu() {
    this.updateContextPanel.cancel();
    this.resetStagedItems();
  }

  handleExpandItemClick({detail: {section}}: ExpanderItemClickEvent) {
    if (section.label === `Hidden Events`) {
      this.trackEvent(TRACKING_EVENTS.LEXICON_SHOW_HIDDEN);
    }
  }

  handleItemFocus({detail}: ItemFocusEvent<EventItem>) {
    this.updateContextPanel(detail.item.value);
  }

  async triggerModal(modalType: ModalType): Promise<void> {
    if (modalType === ModalType.CreateCustomEvent) {
      this.onModalOpen();
      const {appliedValue} = await promptCustomEvents({
        open: true,
        'all-data-groups': this.attr(`all-data-groups`),
        'project-has-people': this.attr(`project-has-people`),
        'project-id': getProjectId(),
        'workspace-id': getWorkspaceId(),
        'track-name': this.attr(`track-name`),
      });

      if (appliedValue) {
        const eventItem = getCustomEventMenuItem(appliedValue).value;
        this.selectItem(eventItem);
      }
      this.onModalClose();
    }
  }

  selectItem(
    rawItem: EventItem,
    {searchFilter = ``, labelMatch = {}, subLabelMatch = {}, customMatch = {}, topScrollPosition = 0} = {},
  ) {
    const item = filterToBookmarkableEventItem(rawItem);

    this.addRecentItem(ResourceType.Event, item);

    const trackingProps = this.trackingProps(
      item,
      searchFilter,
      labelMatch,
      subLabelMatch,
      customMatch,
      topScrollPosition,
    );
    this.trackSelection(trackingProps);
    this.dispatchEvent(
      new CustomEvent<SelectEventItem>(`select`, {detail: {item}}),
    );
    this.resetStagedItems();
  }

  handleClickItem({detail}: ItemClickEvent<EventMenuItem>) {
    const {item} = detail;
    /** Signal is bypassing the sections for this dropdown, return whatever Signal provided */
    if (this.attr(`sections`)?.length > 0) {
      this.dispatchEvent(
        new CustomEvent<SelectEventItem>(`select`, {detail: {item: item.value}}),
      );
      return;
    }

    switch (item.value.action) {
      case ActionType.StageMenu:
        this.stageItem(item.value);
        break;
      case ActionType.TriggerModal:
        this.triggerModal(item.value.modalType);
        break;
      case ActionType.Select:
      case undefined:
        this.selectItem(item.value, detail);
        break;
      default:
        assertUnreachable(item.value);
    }
  }

  getTooltipData(item: EventItem): ContextPanelData {
    const tooltipData = this.getContext(`eventsController`).getContextPanelData(
      item,
      this.getRecentItems(ResourceType.Event),
      {
        allowEdit: !this.attr(`readonly`) && !this.attr(`hide-edit-controls`),
      },
    );

    return tooltipData;
  }

  private handleSearchInput(searchFilter) {
    const isStartingSearch = Boolean(searchFilter && !this.state.searchFilter);
    const isClearingSearch = Boolean(this.state.searchFilter && !searchFilter);
    this.update({searchFilter});
    if (isStartingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_STARTED);
    } else if (isClearingSearch) {
      this.trackEvent(TRACKING_EVENTS.SEARCH_CLEARED);
    }
  }

  private debouncedSearchInput = debounce((searchInput) => this.handleSearchInput(searchInput), 200);

  private updateContextPanel = debounce(
    (focusedItem) => {
      this.dispatchEvent(new CustomEvent(`openContextMenu`, {detail: {isOpen: true}, bubbles: true}));
      this.update({tooltipData: this.getTooltipData(focusedItem)});
    },
    250,
    {leading: true},
  );

  public hasStagedItem(): boolean {
    return this.state.stagedItemsStack.length > 0;
  }

  private getStagedItem(): StageableMenuItem {
    const {stagedItemsStack} = this.state;
    return stagedItemsStack[stagedItemsStack.length - 1];
  }

  public unstageProperty(): void {
    this.produceUpdate((draftState) => {
      draftState.stagedItemsStack.pop();
      draftState.searchFilter = ``;
    });
    this.searchInputEl?.focus();
  }

  private stageItem(item: StageableMenuItem): void {
    this.produceUpdate((draftState) => {
      draftState.stagedItemsStack.push(item);
      draftState.searchFilter = ``;
    });
    this.searchInputEl?.focus();
  }

  private resetStagedItems(): void {
    this.update({stagedItemsStack: []});
  }

  getSelectedHoverTooltipData(): ContextPanelData {
    const selected = this.attr(`selected`);
    return selected && selected.value ? this.getTooltipData(selected) : null;
  }

  getSelectedTrackingProps(): Obj {
    const selected = this.attr(`selected`);
    if (!selected) {
      return null;
    }

    const props = {'Entity type': `event`};
    if (selected.custom) {
      props[`Is custom`] = true;
    }
    return props;
  }

  getPersistenceKey() {
    return `events-menu`;
  }

  getSections() {
    // TODO: Zihe remove this check, a quick and temp workaround for signal to display context panel
    if (this.attr(`sections`)?.length > 0) {
      return this.attr(`sections`);
    }

    const hasTagFilter = this.state.selectedTagFilter.value !== AllEventsFilter.value.value;

    return [
      /* @todo qb-menus -- re-enable properties menu tests in storybook */
      // ...([this.getRecentsSection()]),
      ...this.getContext(`eventsController`).getSections({
        stagedItem: this.getStagedItem(),
        dataGroupId: this.attr(`data-group-id`),
        excludeAutotrackEvents: this.attr(`exclude-autotrack-events`),
        excludeCustomEvents: this.attr(`exclude-custom-events`),
        mainSectionShowLimit: 0,
        searchFilter: this.state.searchFilter,
        selected: this.attr(`selected`),
        showAllEvents: this.attr(`show-all-events`) && !hasTagFilter,
        showAnyEvent: this.attr(`show-any-event`) && !hasTagFilter,
        showSessionEvent: this.attr(`show-session-event`),
        showSpecialDatasetProperty: this.attr(`show-special-dataset-property`),
        showTopEvents: this.attr(`show-top-events`) && !hasTagFilter,
        sortByValue: this.canSortItems() ? this.state.sortByValue : null,
        recentlyUsedEvents: this.getRecentItems(ResourceType.Event),
        tagFilter: hasTagFilter ? this.state.selectedTagFilter.value : null,
        otherOptions: this.attr(`other-options`),
      }),
    ].filter(Boolean);
  }

  getTagFilterMenuItems() {
    const tags = this.getContext(`eventsController`).getEventTagsSection(false, null);
    const currentSelection = this.state.selectedTagFilter.value;
    const allEvents =
      currentSelection === AllEventsFilter.value.value ? {...AllEventsFilter, isSelected: true} : AllEventsFilter;

    const sections = [
      {label: `Filter by`, items: [allEvents]},
      {
        label: `Filter by Tag`,
        items: tags?.items?.map((tag) => ({
          ...tag,
          hasCaret: false,
          isSelected: this.state.selectedTagFilter.value === tag.value.value,
        })),
      },
    ];

    return sections;
  }

  getRecentsSection(): Section<EventItem> {
    const eventData = this.getContext(`eventsController`).data;
    const recentItems = this.getRecentItems(ResourceType.Event, 3) as EventItem[];
    return {
      label: `Recently viewed`,
      items: recentItems
        .map((event) => {
          // prevents deleted events from showing up in recently selected items
          const isCustomEvent = Boolean(event?.id);
          const eventItem = eventData.get(event?.value) ?? (isCustomEvent ? null : getEventMenuItem(event));
          if (eventItem) {
            return produce(eventItem, (updatedEventItem) => {
              updatedEventItem.value.isRecentlyUsed = true;
            });
          } else {
            return null;
          }
        })
        .filter(Boolean),
    };
  }

  selectedLeftIcon() {
    return getIconForEvent(this.state.selected);
  }

  selectedLabel() {
    return getEventSelectedLabel(this.state.selected as Event, {
      includeNameOnRename: true,
      labelPrefix: this.state.selected?.labelPrefix,
    });
  }

  trackingProps(selected, searchFilter, labelMatch, subLabelMatch, customMatch, topscrollPosition) {
    const extraData = this.getTooltipData(selected);
    const extraEventTrackingProps = {
      'num of images': extraData.imageInfo?.length,
    };

    return {
      eventName: `[mp-query-builder] Select event`,
      props: {
        'Event name': selected.label,
        'from tag': this.getStagedItem()?.menuType === MenuType.Tag,
        'top scroll': topscrollPosition,
        'is custom event': !!selected.custom,
        'is autotrack event': !!selected.isCollectEverything,
        'is hidden entity': !!selected.hidden,
        'is recently viewed': !!selected.isRecentlyUsed,
        'Search - filter': searchFilter,
        'Search - label match': labelMatch,
        'Search - sublabel match': subLabelMatch,
        'Search - custom match': customMatch,
        'Metadata Search Enabled': true,
        'Elapsed time (ms) since menu opened': new Date().getTime() - this._openedAt,
        ...extraEventTrackingProps,
      },
    };
  }

  public canSortItems(): boolean {
    return !this.hasStagedItem();
  }
}

export const EventsMenuEl = defineAndMakeJsxFactory(`mp-events-menu`, EventsMenu);
export const EventsMenuReact = panel2react(`mp-events-menu`, EventsMenu);
